﻿<script>
	import model from "../../stores/modelStore";
	import { registerPageComplete } from "../../tealium";
	import { projectSettings, yesNoUnsureOptions } from "../../config";
	import { isValid } from "../../validation";
	import lookups from "../../stores/lookupStore";
	import { monthListOptions, yearListOptions } from "../../config/dateOptions";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import Select from "../../components/Select.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";

	// props
	export let persistUrl = null;
	export let pageName = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}
	let buttons = null;

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};
	
	// For mental health conditions, we will maintain a separate array of selected lookup IDs 
	// to sync with the model's claimDetails.mentalHealthConditions array.
	let selectedMentalHealthConditionIds = [];
	let selectedImpactDiagnosisIds = [];

	const initialise = () => {
		if (!$model.claimDetails) {
			$model.claimDetails = {
				mentalHealthConditions: [],
				impactDiagnoses: [],
			};
		}

		const conditions = $model.claimDetails.mentalHealthConditions ?? [];

		$model.claimDetails.mentalHealthConditions = conditions.map(condition =>
			typeof condition === "number"
				? { lookupId: condition, month: null, year: null }
				: condition
		);

		selectedMentalHealthConditionIds =
			$model.claimDetails.mentalHealthConditions.map(condition => Number(condition.lookupId));


		$model.claimDetails.impactDiagnoses = ($model.claimDetails.impactDiagnoses ?? []).map(diagnosis =>
			typeof diagnosis === "number"
				? { lookupId: diagnosis, month: null, year: null }
				: diagnosis
		);

		selectedImpactDiagnosisIds =
			$model.claimDetails.impactDiagnoses.map(diagnosis => Number(diagnosis.lookupId));


		$model.claimDetails.impactDiagnoses ??= [];
	};

	function syncMentalHealthConditions() {
		const existingByLookupId = new Map(
			($model.claimDetails.mentalHealthConditions ?? [])
				.map(condition => [Number(condition.lookupId), condition])
		);

		$model.claimDetails.mentalHealthConditions =
			selectedMentalHealthConditionIds.map(lookupId =>
				existingByLookupId.get(Number(lookupId)) ?? {
					lookupId: Number(lookupId),
					month: null,
					year: null,
				}
			);
	}

	function handleMentalHealthConditionChange(event) {
		const changedLookupId = Number(event.target.value);
		const currentIds = selectedMentalHealthConditionIds.map(Number);

		const nextIds = event.target.checked
			? [...new Set([...currentIds, changedLookupId])]
			: currentIds.filter(id => id !== changedLookupId);

		selectedMentalHealthConditionIds = nextIds;

		const existingByLookupId = new Map(
			($model.claimDetails.mentalHealthConditions ?? [])
				.map(condition => [Number(condition.lookupId), condition])
		);

		$model.claimDetails.mentalHealthConditions = nextIds.map(lookupId =>
			existingByLookupId.get(lookupId) ?? {
				lookupId,
				month: null,
				year: null,
			}
		);
	}

	function handleImpactDiagnosisChange(event) {
		const changedLookupId = Number(event.target.value);
		const currentIds = selectedImpactDiagnosisIds.map(Number);

		const nextIds = event.target.checked
			? [...new Set([...currentIds, changedLookupId])]
			: currentIds.filter(id => id !== changedLookupId);

		selectedImpactDiagnosisIds = nextIds;

		const existingByLookupId = new Map(
			($model.claimDetails.impactDiagnoses ?? [])
				.map(diagnosis => [Number(diagnosis.lookupId), diagnosis])
		);

		$model.claimDetails.impactDiagnoses = nextIds.map(lookupId =>
			existingByLookupId.get(lookupId) ?? {
				lookupId,
				month: null,
				year: null,
			}
		);
	}

	initialise();

	// For mental health conditions, check if "Other" is selected to determine 
	// whether to show the text area for additional details.
	$: otherMentalHealthCondition = ($lookups?.mentalHealthConditions ?? [])
		.find(condition => condition.label === "Other");

	$: showMentalHealthConditionsOther =
		!!otherMentalHealthCondition &&
		($model.claimDetails?.mentalHealthConditions ?? []).some(
			selected => Number(selected.lookupId) === Number(otherMentalHealthCondition.value)
		);

	$: if (
		$model.claimDetails?.receivedFormalDiagnosis !== "Yes" &&
		$model.claimDetails?.formalDiagnosisDetails != null
	) {
		$model.claimDetails.formalDiagnosisDetails = null;
	}
</script>

<PageSection>
	<Form {disabled} id="form-claim-details-impact" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Claim Details</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn>
					<CheckboxButtonList
						name="mentalHealthConditions"
						label="What have been the impacts of social media use on your life?"
						sublabel="Select all that apply"
						bind:selected={selectedMentalHealthConditionIds}
						on:change={handleMentalHealthConditionChange}
						optionList={$lookups.mentalHealthConditions}
						isVertical={false}
						validator="checkboxRequired"
						validatorValue="1"
						validatorMessage="At least one selection is required"
						validatorTarget=".culture-error"
						labelProperty="label"
						valueProperty="value"
						{disabled}
					/>
				</FormColumn>
			</FormRow>


			{#if showMentalHealthConditionsOther}
				<FormRow>
					<FormColumn>
						<TextArea
							{disabled}
							label="Please explain?"
							bind:value={$model.claimDetails.mentalHealthConditionsOther}
							id="mentalHealthConditionsOther" />
					</FormColumn>
				</FormRow>
			{/if}

			<FormRow>
				<FormColumn>
					<Select
						required
						{disabled}
						label="Have you ever been formally diagnosed with a psychiatric, behavioural or physical injury linked with your social media use?"
						bind:value={$model.claimDetails.receivedFormalDiagnosis}
						optionList={yesNoUnsureOptions}
						textProperty="label"
						valueProperty="value" />
				</FormColumn>
			</FormRow>

			{#if $model.claimDetails.receivedFormalDiagnosis === "Yes"}
			

				<FormRow>
					<FormColumn>
						<CheckboxButtonList
							name="impactDiagnosis"
							label="What diagnosis did you receive?"
							sublabel="Select all that apply"
							bind:selected={selectedImpactDiagnosisIds}
							on:change={handleImpactDiagnosisChange}
							optionList={$lookups.impactDiagnoses}
							isVertical={false}
							validator={$model.claimDetails.receivedFormalDiagnosis === "Yes" ? "checkboxRequired" : null}
							validatorValue="1"
							validatorMessage="At least one selection is required"
							validatorTarget=".culture-error"
							labelProperty="label"
							valueProperty="value"
							{disabled}
						/>
					</FormColumn>
				</FormRow>

				{#if $model.claimDetails.impactDiagnoses.length > 0}
					<FormRow style="margin-bottom:0px;">
						<FormColumn>
							<p>Please provide the approximate month and year of each diagnosis selected above</p>
						</FormColumn>
					</FormRow>
				{/if}

				{#each $model.claimDetails.impactDiagnoses as diagnosis (diagnosis.lookupId)}
					{@const diagnosisLookup = ($lookups?.impactDiagnoses ?? [])
						.find(item => Number(item.value) === Number(diagnosis.lookupId))}

					<FormRow>
						<FormColumn class="d-flex align-items-center" cols="4">
							<p><strong>{diagnosisLookup?.label ?? ""}</strong></p>
						</FormColumn>
						<FormColumn cols="4">
							<Select
								{disabled}
								required
								label="Month"
								bind:value={diagnosis.month}
								optionList={monthListOptions}
								headOptionText="Select a month"
								id={`impactDiagnosisMonth-${diagnosis.lookupId}`}
							/>
						</FormColumn>
						<FormColumn cols="4">
							<Select
								{disabled}
								required
								label="Year"
								bind:value={diagnosis.year}
								optionList={yearListOptions}
								headOptionText="Select a year"
								id={`impactDiagnosisYear-${diagnosis.lookupId}`}
							/>
						</FormColumn>
					</FormRow>
				{/each}
			{/if}
		</div>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>